// src/components/SideBar.jsx
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Typography,
  useColorScheme,
} from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import {
  AiOutlineBarChart,
  AiOutlineDashboard,
  AiOutlineDollar,
  AiOutlineMenuFold,
  AiOutlineSchedule,
  AiOutlineSetting,
  AiOutlineVerified,
  AiOutlineWallet,
} from "react-icons/ai";
import { FaGithub, FaInstagram, FaLinkedin, FaTwitter } from "react-icons/fa";
import { NavLink, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext"; // <-- ADDED
import sideLogo from "../assets/money-bags-of-three-currencies-svgrepo-com.svg";
import whiteLogo from "../assets/white money bag.svg";

const navItems = [
  { text: "Overview", icon: <AiOutlineDashboard />, path: "/dashboard" },
  { text: "Transactions", icon: <AiOutlineWallet />, path: "/transactions" },
  // { text: "Categories", icon: <AiOutlineCaretRight />, path: "/categories" },
  { text: "Budgets", icon: <AiOutlineDollar />, path: "/budgets" },
  { text: "Reports", icon: <AiOutlineBarChart />, path: "/reports" },
  {
    text: "Scheduled Reports",
    icon: <AiOutlineSchedule />,
    path: "/scheduled-reports",
  },
  { text: "Verify Email", icon: <AiOutlineVerified />, path: "/verify-email" },
  { text: "Settings", icon: <AiOutlineSetting />, path: "/settings" },
];

// Paths to be disabled (unclickable) until further notice
const disabledPaths = [
  // "/budgets",
  "/reports",
  "/charts",
  "/verify-email",
  "/settings",
  "/scheduled-reports",
];

const SideBar = ({ user = {}, setDrawerIsOpen = () => {} }) => {
  const location = useLocation(); // ✅ inside component

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm")); // xs / small
  const isMedium = useMediaQuery(theme.breakpoints.down("md")); // tablet / medium
  const isLarge = useMediaQuery(theme.breakpoints.down("lg"));
  const isMdToLg = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const { mode, setMode } = useColorScheme();

  const [open, setOpen] = useState(false);

  // Get both user and completeProfile from context
  const { user: ctxUser, completeProfile } = useContext(AuthContext);
  const effectiveUser = ctxUser ?? user;
  const [visible, setVisible] = useState(Boolean(effectiveUser));

  // Keep visible in sync when user changes (login/logout)
  useEffect(() => {
    setVisible(Boolean(effectiveUser));
  }, [effectiveUser]);

  // Listen for auth:logout (dispatched by your axios interceptor) and storage events from other tabs
  useEffect(() => {
    const closeUi = () => {
      setOpen(false);
      setDrawerIsOpen(false);
      setVisible(false);
    };

    const onAuthLogout = () => {
      closeUi();
    };

    const onStorage = (e) => {
      if (e.key === "token" && e.newValue === null) {
        closeUi();
      }
    };

    window.addEventListener("auth:logout", onAuthLogout);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("auth:logout", onAuthLogout);
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // --- END USER VISIBILITY LOGIC ---

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
    setDrawerIsOpen(newOpen);
  };

  // If not visible (logged out), don't render anything
  if (!visible) return null;

  // If the user object contains social links, use them; otherwise fallbacks to '#'
  // Expected shape (optional): user.socials = { twitter, github, linkedin, instagram }
  const socials = effectiveUser?.socials || {};
  const socialLinks = {
    twitter: socials.twitter || "#",
    github: socials.github || "#",
    linkedin: socials.linkedin || "#",
    instagram: socials.instagram || "#",
  };
  // console.log(user);

  const renderNavItem = ({ text, icon, path }) => {
    // Profile incomplete? Disable everything except Overview
    const isProfileIncomplete =
      completeProfile === false && path !== "/dashboard";
    const isDisabled = disabledPaths.includes(path) || isProfileIncomplete;
    const isActive = location.pathname === path;

    if (isDisabled) {
      return (
        <React.Fragment key={text}>
          <ListItemButton
            component="div"
            aria-disabled="true"
            title={
              isProfileIncomplete
                ? "Complete your profile first"
                : "This page is currently disabled"
            }
            sx={{
              borderRadius: 1,
              mx: 1,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              alignContent: "center",
              pointerEvents: "none",
              opacity: 0.45,
              cursor: "not-allowed",
              backgroundColor: isActive
                ? mode === "light"
                  ? "neutral.200"
                  : "background.backdrop"
                : "transparent",
            }}
          >
            <ListItemDecorator
              sx={{ color: mode === "light" ? "black" : "white" }}
            >
              {icon}
            </ListItemDecorator>
            <ListItemContent
              primaryTypographyProps={{
                sx: {
                  fontFamily: "monospace",
                  color: mode === "light" ? "black" : "white",
                },
              }}
            >
              {text}
            </ListItemContent>
          </ListItemButton>
          <Divider sx={{ color: "white", width: "90%", mx: "auto" }} />
        </React.Fragment>
      );
    }
  };
  // Drawer content (for medium screens)
  const DrawerList = (
    <Grid
      sx={{
        px: 2,
        py: 2,
        width: "100%",
        zIndex: 9999,
        backgroundColor: mode === "light" ? "#f0f0f0" : "hsl(0, 0%, 6%)",
        height: "100%",
      }}
      role="presentation"
      onClick={toggleDrawer(false)}
    >
      <Grid
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          justifyContent: "center",
          backgroundColor: mode === "light" ? "#f0f0f0" : "hsl(0, 0%, 6%)",
          borderRadius: "5px",
          width: "100%",
        }}
      />

      <Grid display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <Avatar src={effectiveUser?.profile_picture} />
        <Typography
          sx={{
            fontFamily: "monospace",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {effectiveUser?.first_name || effectiveUser?.last_name
            ? `${effectiveUser.first_name || ""} ${effectiveUser.last_name || ""}`.trim()
            : "Hello stranger :)"}
          <Typography sx={{ fontFamily: "monospace", fontSize: 12 }}>
            {effectiveUser?.name || ""}
          </Typography>
        </Typography>
      </Grid>

      <List sx={{ pb: 10 }}>
        {navItems.map(({ text, icon, path }) => {
          const isProfileIncomplete =
            completeProfile === false && path !== "/dashboard";
          const isDisabled =
            disabledPaths.includes(path) || isProfileIncomplete;
          const isActive = location.pathname === path;

          if (isDisabled) {
            return (
              <React.Fragment key={text}>
                <ListItemButton
                  component="div"
                  aria-disabled="true"
                  title={
                    isProfileIncomplete
                      ? "Complete your profile first"
                      : "This page is currently disabled"
                  }
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    alignContent: "center",
                    pointerEvents: "none",
                    opacity: 0.45,
                    cursor: "not-allowed",
                    backgroundColor: isActive
                      ? mode === "light"
                        ? "neutral.200"
                        : "background.backdrop"
                      : "transparent",
                  }}
                >
                  <ListItemDecorator
                    sx={{ color: mode === "light" ? "black" : "white" }}
                  >
                    {icon}
                  </ListItemDecorator>
                  <ListItemContent
                    primaryTypographyProps={{
                      sx: {
                        fontFamily: "monospace",
                        color: mode === "light" ? "black" : "white",
                      },
                    }}
                  >
                    {text}
                  </ListItemContent>
                </ListItemButton>
                <Divider sx={{ color: "white", width: "90%", mx: "auto" }} />
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={text}>
              <ListItemButton
                component={NavLink}
                to={path}
                sx={{
                  borderRadius: isActive ? 8 : 1,
                  mx: 1,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  alignContent: "center",
                  textDecoration: "none",
                  backgroundColor: isActive
                    ? mode === "light"
                      ? "neutral.200"
                      : "background.backdrop"
                    : "transparent",
                }}
              >
                <ListItemDecorator
                  sx={{ color: mode === "light" ? "black" : "white" }}
                >
                  {icon}
                </ListItemDecorator>
                <ListItemContent
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: "monospace",
                      color: mode === "light" ? "black" : "white",
                    },
                  }}
                >
                  {text}
                </ListItemContent>
              </ListItemButton>
              <Divider sx={{ color: "white", width: "90%", mx: "auto" }} />
            </React.Fragment>
          );
        })}
      </List>
      <img
        src={mode === "light" ? sideLogo : whiteLogo}
        alt="FinTrack logo"
        style={{
          height: "20vh", // controls size
          width: "auto", // keeps aspect ratio
          maxWidth: "100%",
          objectFit: "contain",
          display: "block",
          justifySelf: "center",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          left: 16,
          right: 16,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "center",
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            aria-label="GitHub"
            component="a"
            href={socialLinks.github}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            size="sm"
            variant="plain"
            sx={{ color: mode === "light" ? "#333" : "white" }}
          >
            <FaGithub />
          </IconButton>

          <IconButton
            aria-label="LinkedIn"
            component="a"
            href={socialLinks.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            size="sm"
            variant="plain"
            sx={{ color: mode === "light" ? "#0A66C2" : "#0A66C2" }}
          >
            <FaLinkedin />
          </IconButton>

          <IconButton
            aria-label="Instagram"
            component="a"
            href={socialLinks.instagram}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            size="sm"
            variant="plain"
            sx={{ color: mode === "light" ? "#E1306C" : "#E1306C" }}
          >
            <FaInstagram />
          </IconButton>
        </Box>

        <Typography
          level="body-xs"
          sx={{ fontFamily: "monospace", fontSize: 12, textAlign: "center" }}
        >
          © {new Date().getFullYear()} FinTrack. All rights reserved.
        </Typography>
        <Typography
          level="body-xs"
          sx={{ fontFamily: "monospace", fontSize: 12 }}
        >
          Developed by{" "}
          <a
            href="https://houssem-rezgui-portfolio.vercel.app/"
            target="_blank"
            rel="noreferrer noopener"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Houssem Rezgui
          </a>
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <>
      {/* --- SMALL SCREENS (xs / mobile) : bottom bar --- */}
      {isSmall ? (
        <Box
          component="nav"
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1400,
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            height: 64,
            py: 0.5,
            px: 1,
            gap: 0,
            backgroundColor: mode === "light" ? "#ffffff" : "hsl(0, 0%, 1%)",
            borderTop:
              mode === "light"
                ? "1px solid rgba(0,0,0,0.06)"
                : "1px solid rgba(255,255,255,0.06)",
            boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
          }}
          aria-label="Bottom navigation"
        >
          {navItems.map(({ text, icon, path }) => {
            const isProfileIncomplete =
              completeProfile === false && path !== "/dashboard";
            const isDisabled =
              disabledPaths.includes(path) || isProfileIncomplete;
            const isActive = location.pathname === path;

            if (isDisabled) {
              return (
                <Box
                  key={text}
                  component="div"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.2,
                    minWidth: { xs: 44, sm: 56 },
                    maxWidth: { xs: 80, sm: 120 },
                    flex: "1 1 auto",
                    opacity: 0.45,
                    pointerEvents: "none",
                    px: 0.5,
                    overflow: "hidden",
                    textAlign: "center",
                  }}
                  title={
                    isProfileIncomplete
                      ? "Complete your profile first"
                      : "This page is currently disabled"
                  }
                >
                  <ListItemDecorator
                    sx={{
                      fontSize: { xs: 16, sm: 15 },
                      lineHeight: 1,
                      color: mode === "light" ? "#454545" : null,
                    }}
                  >
                    {icon}
                  </ListItemDecorator>
                </Box>
              );
            }

            return (
              <ListItemButton
                key={text}
                component={NavLink}
                to={path}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.2,
                  /* responsive sizing so items don't overflow on xs */
                  minWidth: { xs: 44, sm: 56 },
                  maxWidth: { xs: 80, sm: 120 },
                  flex: "1 1 auto", // allow items to shrink/grow instead of overflowing
                  textDecoration: "none",
                  color: "inherit",
                  py: 0.5,
                  px: 0.5,
                  overflow: "hidden", // keep content inside bounds
                  backgroundColor: isActive
                    ? mode === "light"
                      ? "neutral.200"
                      : "background.backdrop"
                    : "transparent",
                }}
              >
                <ListItemDecorator
                  sx={{
                    fontSize: { xs: 18, sm: 20 },
                    lineHeight: 1,
                    color: mode === "light" ? "#454545" : null,
                  }}
                >
                  {icon}
                </ListItemDecorator>
              </ListItemButton>
            );
          })}
        </Box>
      ) : isMedium ? (
        // --- MEDIUM SCREENS (tablet) : keep your IconButton + Drawer behavior (unchanged) ---
        <div>
          <IconButton
            onClick={toggleDrawer(true)}
            disableRipple
            sx={{
              display: "flex",
              justifyContent: "stretch",
              position: "fixed",
              height: "100%",
              backgroundColor: "transparent", // base
              boxShadow: "none",
              zIndex: 1400,

              // interaction states
              "&:hover": {
                backgroundColor: "transparent",
                boxShadow: "none",
              },
              // some MUI implementations use this class for keyboard focus
              "&.Mui-focusVisible": {
                backgroundColor: "transparent",
                boxShadow: "none",
              },
              "&:active": {
                backgroundColor: "transparent",
                boxShadow: "none",
              },
            }}
            aria-label="Open navigation"
            size="sm"
          >
            <AiOutlineMenuFold />
          </IconButton>
          <Drawer
            open={open}
            onClose={toggleDrawer(false)}
            variant="soft"
            size={isSmall ? "sm" : isMedium ? "md" : "lg"}
            sx={{ zIndex: 99999 }}
            PaperProps={{
              sx: {
                backgroundColor: mode === "light" ? "#f0f0f0" : "#051423",
                height: "100%",
              },
            }}
          >
            {DrawerList}
          </Drawer>
        </div>
      ) : (
        // --- LARGE / DESKTOP (unchanged) ---
        <Grid
          sx={{
            display: "flex",
            flexDirection: "column",
            px: isLarge ? 0 : 2,
            py: 2,
            // bgcolor:
            //   mode === "light" ? "#ebebeb" : "hsla(240, 7.70%, 2.50%, 0.15)",
            borderRight:
              mode === "light"
                ? "0.2px solid hsla(240, 7.70%, 2.50%, 0.15)"
                : "0.2px solid rgba(229, 231, 235, 0.14)",
            gap: 2,
            width: isLarge ? "15%" : "20%",
            mt: "1%",
            borderRadius: "2px",
            height: "89%",
            position: "fixed",
            // left: 16,
            // top: 16,
            // bottom: 16,
            // boxSizing: "border-box",
            left: isLarge ? 0 : null,
          }}
        >
          <Grid
            display="flex"
            alignItems="center"
            gap={2}
            flexDirection={isLarge ? "column" : null}
          >
            <Avatar src={effectiveUser?.profile_picture} />
            <Typography
              sx={{
                fontFamily: "monospace",
                display: "flex",
                flexDirection: "column",
                fontSize: isLarge ? "0.7rem" : null,
                alignItems: isLarge ? "center" : null,
              }}
            >
              {effectiveUser?.first_name || effectiveUser?.last_name
                ? `${effectiveUser.first_name || ""} ${effectiveUser.last_name || ""}`.trim()
                : "Hello stranger :)"}
              <Typography
                sx={{
                  fontFamily: "monospace",
                  color: mode === "light" ? "#454545" : null,
                  fontSize: isLarge ? 10 : 12,
                }}
              >
                {effectiveUser?.name || ""}
              </Typography>
            </Typography>
          </Grid>

          <List sx={{ overflowY: "auto", display: "flex" }}>
            {navItems.map(({ text, icon, path }) => {
              const isProfileIncomplete =
                completeProfile === false && path !== "/dashboard";
              const isDisabled =
                disabledPaths.includes(path) || isProfileIncomplete;
              const isActive = location.pathname === path;

              if (isDisabled) {
                return (
                  <React.Fragment key={text}>
                    <ListItemButton
                      component="div"
                      aria-disabled="true"
                      title={
                        isProfileIncomplete
                          ? "Complete your profile first"
                          : "This page is currently disabled"
                      }
                      sx={{
                        borderRadius: "7px",
                        mb: 0,
                        display: "flex",
                        flexDirection: isMedium || isLarge ? "column" : "row",
                        alignItems: "center",

                        pointerEvents: "none",
                        opacity: 0.45,
                        cursor: "not-allowed",
                        left: 0,
                      }}
                    >
                      <ListItemDecorator
                        sx={{ color: "inherit", fontSize: 20 }}
                      >
                        {icon}
                      </ListItemDecorator>
                      <ListItemContent
                        sx={{
                          fontFamily: "monospace",
                          fontSize: isLarge ? "0.7rem" : "1rem",
                        }}
                      >
                        {text}
                      </ListItemContent>
                    </ListItemButton>
                    <Divider
                      sx={{ color: "inherit", width: "90%", mx: "auto" }}
                    />
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={text}>
                  <ListItemButton
                    component={NavLink}
                    to={path}
                    sx={{
                      borderRadius: "7px",
                      mb: 0,
                      display: "flex",
                      flexDirection: isMedium || isLarge ? "column" : "row",
                      alignItems: "center",
                      textDecoration: "none",
                      left: 0,
                      backgroundColor: isActive
                        ? mode === "light"
                          ? "neutral.200"
                          : "background.backdrop"
                        : "transparent",
                       
                    }}
                  >
                    <ListItemDecorator sx={{ color: "inherit", fontSize: 20 }}>
                      {icon}
                    </ListItemDecorator>
                    <ListItemContent
                      sx={{
                        fontFamily: "monospace",
                        fontSize: isLarge ? "0.7rem" : "1rem",
                      }}
                    >
                      {text}
                    </ListItemContent>
                  </ListItemButton>
                  <Divider
                    sx={{ color: "inherit", width: "90%", mx: "auto" }}
                  />
                </React.Fragment>
              );
            })}
               <img
            src={mode === "light" ? sideLogo : whiteLogo}
            alt="FinTrack logo"
            style={{
              height: isMdToLg ? "45px" : "15vh", // controls size
              width: "auto", // keeps aspect ratio
              maxWidth: "100%",
              objectFit: "contain",
              display: "flex",
              alignSelf:"center",
              justifySelf:"center",
              marginTop:"40%"
            }}
          />
          </List>
       
          {/* Footer pinned to bottom via mt: 'auto' */}
          {/* <Divider /> */}
          <Grid
            container
            sx={{
              mt: "auto",
              pt: 1,
              // borderTop: "1px solid rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                aria-label="GitHub"
                component="a"
                href={socialLinks.github}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                variant="plain"
                sx={{ color: mode === "light" ? "#333" : "white" }}
              >
                <FaGithub />
              </IconButton>

              <IconButton
                aria-label="LinkedIn"
                component="a"
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                variant="plain"
                sx={{ color: "#0A66C2" }}
              >
                <FaLinkedin />
              </IconButton>

              <IconButton
                aria-label="Instagram"
                component="a"
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                variant="plain"
                sx={{ color: "#E1306C" }}
              >
                <FaInstagram />
              </IconButton>
            </Box>

            <Typography
              level="body-xs"
              sx={{
                fontFamily: "monospace",
                fontSize: 12,
                textAlign: "center",
              }}
            >
              © {new Date().getFullYear()} FinTrack. All rights reserved.
              <br />
              <Typography
                level="body-xs"
                sx={{ fontFamily: "monospace", fontSize: 12 }}
              >
                Developed by{" "}
                <a
                  href="https://houssem-rezgui-portfolio.vercel.app/"
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: "inherit", textDecoration: "underline" }}
                >
                  Houssem Rezgui
                </a>
              </Typography>
            </Typography>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default SideBar;
